import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../api/reports';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import { Card, PageHead } from '../components/ui';
import { StoreSelector } from '../components/StoreSelector';
import {
  DateRangePicker,
  computeRange,
  type DateRange,
} from '../components/DateRangePicker';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { downloadCsv } from '../lib/download';
import { count, money } from '../lib/format';

export function SalesReportPage() {
  const { isSuperAdmin, user } = useAuth();
  const { selectedStoreId } = useStores();
  const [range, setRange] = useState<DateRange>(() => computeRange('today'));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const storeId = isSuperAdmin ? selectedStoreId : user?.storeId ?? null;
  const from = range.from;
  const to = range.to;
  // On a custom range the user may add a time-of-day. When set, send precise
  // ISO timestamps; the server uses them exactly (else it expands the plain
  // dates to whole days). The end time is inclusive of the chosen minute.
  const fromDateTime =
    range.preset === 'custom' && range.fromTime
      ? `${range.from}T${range.fromTime}:00`
      : undefined;
  const toDateTime =
    range.preset === 'custom' && range.toTime
      ? `${range.to}T${range.toTime}:59.999`
      : undefined;

  const q = useQuery({
    queryKey: ['reports', 'store-wise', from, to, fromDateTime, toDateTime, storeId],
    queryFn: () =>
      reportsApi.storeWise({ from, to, fromDateTime, toDateTime, storeId }),
  });

  const totals = (q.data ?? []).reduce(
    (acc, r) => ({
      orders: acc.orders + r.orders,
      grossSales: acc.grossSales + r.grossSales,
      discount: acc.discount + r.discount,
      gst: acc.gst + r.gst,
      netSales: acc.netSales + r.netSales,
    }),
    { orders: 0, grossSales: 0, discount: 0, gst: 0, netSales: 0 },
  );

  async function onExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCsv(
        '/reports/store-wise/export',
        {
          from,
          to,
          fromDateTime,
          toDateTime,
          storeId: storeId ?? undefined,
        },
        `store-wise_${range.from}_${range.to}.csv`,
      );
    } catch (err) {
      setExportError(toErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <PageHead
        title="Sales Reports"
        subtitle="Store-wise sales over a date range."
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => window.print()}
            >
              🖨️ Print
            </button>
            <button
              className="btn btn-primary"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : '⬇ Export CSV'}
            </button>
          </>
        }
      />

      <div className="toolbar no-print">
        <DateRangePicker value={range} onChange={setRange} />
        <div className="spacer" />
        {isSuperAdmin && <StoreSelector allowAll label="Store" />}
      </div>

      {exportError && <div className="form-error">{exportError}</div>}

      <div className="print-area">
        <div className="hint" style={{ marginBottom: 10 }}>
          {fromDateTime || toDateTime
            ? `${range.from} ${range.fromTime ?? '00:00'} → ${range.to} ${range.toTime ?? '23:59'}`
            : range.from === range.to
              ? range.from
              : `${range.from} → ${range.to}`}
        </div>
        <Card title="Store-wise Sales">
          {q.isLoading ? (
            <Loading />
          ) : q.isError ? (
            <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
          ) : !q.data || q.data.length === 0 ? (
            <Empty icon="📈" title="No sales in this period" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Store</th>
                    <th className="num">Orders</th>
                    <th className="num">Gross Sales</th>
                    <th className="num">Discount</th>
                    <th className="num">GST</th>
                    <th className="num">Net Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.map((r) => (
                    <tr key={r.storeId}>
                      <td>
                        <strong>{r.storeName}</strong>
                      </td>
                      <td className="num">{count(r.orders)}</td>
                      <td className="num">{money(r.grossSales)}</td>
                      <td className="num">{money(r.discount)}</td>
                      <td className="num">{money(r.gst)}</td>
                      <td className="num">{money(r.netSales)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td className="num">{count(totals.orders)}</td>
                    <td className="num">{money(totals.grossSales)}</td>
                    <td className="num">{money(totals.discount)}</td>
                    <td className="num">{money(totals.gst)}</td>
                    <td className="num">{money(totals.netSales)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
